import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { toast } from '@/app/components/ui/simple-toaster';
import { InspectionForm } from '@/components/inspections/inspection-form';
import { useInspection, useUpdateInspection } from '@/hooks/use-inspections';
import { useAssets } from '@/hooks/use-assets';
import { PageBreadcrumb } from '@/app/components/page-breadcrumb';
import type { CreateInspectionPayload } from '@/lib/types';

export function EditInspectionPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [assetSearch, setAssetSearch] = useState('');

  const inspectionId = parseInt(id || '0', 10);

  const { data: inspection, isLoading: inspectionLoading, isError } = useInspection(inspectionId);
  const { data: assetsData, isLoading: assetsLoading } = useAssets({
    page_size: 50,
    q: assetSearch,
  });

  const updateMutation = useUpdateInspection();

    const handleSubmit = async (data: CreateInspectionPayload) => {
      try {
        await updateMutation.mutateAsync({ id: inspectionId, data });
        toast.success('Inspection updated successfully');
        navigate(`/inspections/${inspectionId}`);
      } catch (_error) {
        toast.error('Failed to update inspection. Please try again.');
      }
    };

  const handleSearchAssets = (query: string) => {
    setAssetSearch(query);
  };

  const assets = assetsData?.results || [];

  if (inspectionLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError || !inspection) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card>
          <CardContent className="p-12 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Inspection Not Found</h2>
            <p className="text-gray-600 mb-4">
              The inspection you're trying to edit doesn't exist or you don't have permission to edit it.
            </p>
            <Button onClick={() => navigate('/inspections')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Inspections
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageBreadcrumb items={[
        { label: 'Inspections', href: '/inspections' },
        { label: 'Inspection', href: `/inspections/${inspectionId}` },
        { label: 'Edit' },
      ]} />
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/inspections/${inspectionId}`)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Edit Inspection
          </h1>
          <p className="text-gray-600 mt-1">
            Update inspection details and findings
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          <InspectionForm
            inspection={inspection}
            assets={assets}
            assetsLoading={assetsLoading}
            onSearchAssets={handleSearchAssets}
            onSubmit={handleSubmit}
            isSubmitting={updateMutation.isPending}
            onCancel={() => navigate(`/inspections/${inspectionId}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}
